import { isObject, isFunction } from '@/global/utils/utils'
import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions.js'
import TruncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'

const { buildConfig } = useTableService()
const TABLE_KEY = 'crews'
const API_CONFIG = {
  get: {
    module: 'road-maintenance',
    route: 'crews'
  }
}
const DEFAULT_CELL_EMPTY_VALUE = null

export function useTable (options) {
  const {
    trans,
    router,
    closeInfoDialogFn,
    openInfoDialogFn,
    setInfoDialogLoaderFn
  } = options

  const isCreating = ref(false)

  const startCreating = () => {
    isCreating.value = true
  }

  const resetCreating = () => {
    isCreating.value = false
  }

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.temporary_regulation')
      },
      {
        text: trans('road-maintenance/temporary_regulation.crews'),
        bolded: true
      }
    ]
  }))

  const cellsConfig = computed(() => ({
    vehicle_brand_model: {
      custom_display_value: (data) => data?.vehicle?.label || '-'
    },
    vehicle_registration: {
      custom_display_value: (data) => data?.vehicle?.registration || '-'
    },
    responsible_person: {
      custom_display_value: (data) => data?.responsible_person_name || '-'
    },
    crew: {
      custom_display_value: (data) => data?.members || ''
    },
    status: {
      type: 'status',
      custom_display_value: (data) => {
        if (!data?.status || !isObject(data?.status)) {
          return DEFAULT_CELL_EMPTY_VALUE
        }
        return {
          ...data.status,
          ...{
            description: data.status.description ? trans(data.status.description) : '-',
            // getStatusStyle reads colorObjectKey as a direct property of cellData,
            // but the color object actually lives nested under status.data.color.
            color: data.status.data?.color || null
          }
        }
      },
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background'
    }
  }))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/temporary_regulation/crews.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/crews.crew_name'),
      key: 'name',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/crews.vehicle_brand_model'),
      key: 'vehicle_brand_model',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/crews.vehicle_registration'),
      key: 'vehicle_registration',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/crews.responsible_person'),
      key: 'responsible_person',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/crews.crew'),
      key: 'crew',
      component: TruncatedTextDisplay,
      maxTextLength: 40,
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/crews.status'),
      key: 'status',
      always_visible: true
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans('road-maintenance/temporary_regulation/crews.crew_name')
      }
    ]
  }))

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const deleteCrewHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      setInfoDialogLoaderFn(true)
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`crews/${id}`)
      fetchTableData()
      closeInfoDialogFn()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onCrewDeletion = (event) => {
    openInfoDialogFn({
      title: trans('road-maintenance/temporary_regulation/crews.delete_confirmation_title'),
      message: trans('road-maintenance/temporary_regulation/crews.delete_confirmation_message'),
      onConfirm: () => deleteCrewHandler(event)
    })
  }

  const onShowDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: 'TemporaryRegulationsCrewDetails',
      params: {
        id: String(id)
      }
    })
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    deleteAction(trans, onCrewDeletion)
  ]))

  const config = computed(() => {
    return {
      tableConfig: buildConfig({
        key: TABLE_KEY,
        apiConfig: API_CONFIG,
        rowDblClick: onShowDetails,
        search_and_buttons_config: {
          search_field_config: {
            placeholder: trans('base.search'),
            prependInnerIcon: 'mdi-magnify'
          },
          newClick: startCreating,
          showNewBtn: true,
          newButtonConfig: {
            label: trans('road-maintenance/temporary_regulation/crews.activate_new_crew')
          }
        },
        filters_and_columns_visibility_config: {
          showRefreshButton: false,
          showFiltersButton: false,
          showColumnsVisibilityButton: false
        },
        tableConfig: {
          cellsConfig: cellsConfig.value,
          headers: headers.value,
          actionsConfig: actionsConfig.value,
          filtersConfig: filtersConfig.value
        }
      }),
      breadcrumbs: breadcrumbs.value
    }
  })

  return {
    config,
    isCreating,
    resetCreating,
    fetchTableData
  }
}
